import groupVeilleByMonth from './groupVeilleByMonth';

const PARAMS = { q: 'q', month: 'mois', sort: 'tri', page: 'p' };
const PAGE_BUDGET = 60;

function archivePath() {
  return window.location.pathname.replace(/\/page\/\d+\/?$/, '/') || '/veille/';
}

function readParams() {
  const params = new URLSearchParams(window.location.search);
  const page = Number.parseInt(params.get(PARAMS.page) || '1', 10);
  return {
    q: (params.get(PARAMS.q) || '').trim(),
    month: params.get(PARAMS.month) || '',
    sort: params.get(PARAMS.sort) === 'ancien' ? 'ancien' : 'recent',
    page: Number.isFinite(page) && page > 0 ? page : 1,
  };
}

function veilleLabel(count) {
  return `${count} veille${count > 1 ? 's' : ''}`;
}

function isFiltered({ q, month, sort }) {
  return Boolean(q || month || sort === 'ancien');
}

function toSearchParams({ q, month, sort, page }) {
  const params = new URLSearchParams();
  if (q) {
    params.set(PARAMS.q, q);
  }
  if (month) {
    params.set(PARAMS.month, month);
  }
  if (sort === 'ancien') {
    params.set(PARAMS.sort, 'ancien');
  }
  if (page > 1) {
    params.set(PARAMS.page, String(page));
  }
  return params;
}

function archiveUrl(state) {
  const next = toSearchParams(state).toString();
  return `${archivePath()}${next ? `?${next}` : ''}`;
}

function writeParams(state, { push = false } = {}) {
  const next = archiveUrl(state);
  const current = `${window.location.pathname}${window.location.search}`;
  if (next === current) {
    return;
  }
  const url = `${next}${window.location.hash}`;
  if (push) {
    window.history.pushState(null, '', url);
    return;
  }
  window.history.replaceState(null, '', url);
}

function capitalizeMonthLabel(label) {
  const text = (label || '').trim();
  if (!text) {
    return '';
  }
  return text.charAt(0).toLocaleUpperCase('fr-FR') + text.slice(1);
}

function htmlToSearchText(html) {
  if (!html) {
    return '';
  }
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return (doc.body?.textContent || '').replace(/\s+/g, ' ').trim();
}

function collectArchive(root) {
  if (!root) {
    return [];
  }
  return [...root.querySelectorAll('[data-veille-entry]')].map((el) => {
    const bodyField = el.querySelector('[data-body]');
    const rawBody = bodyField?.value || bodyField?.textContent || '';
    return {
      url: el.getAttribute('data-url'),
      month: el.getAttribute('data-month'),
      monthLabel: capitalizeMonthLabel(el.getAttribute('data-month-label')),
      date: el.getAttribute('data-date'),
      displayDate: el.getAttribute('data-display-date'),
      reading: el.getAttribute('data-reading'),
      title: el.querySelector('[data-title]')?.textContent.trim() || '',
      excerpt: el.querySelector('[data-excerpt]')?.textContent.trim() || '',
      body: htmlToSearchText(rawBody),
    };
  });
}

function dedupeByUrl(entries) {
  const seen = new Set();
  return entries.filter((entry) => {
    if (!entry.url || seen.has(entry.url)) {
      return false;
    }
    seen.add(entry.url);
    return true;
  });
}

const INDEX_PATH = '/data/veille/';
const BODY_INDEX_PATH = '/data/veille-corps/';

function catalogUrl(base, page) {
  const root = base.endsWith('/') ? base : `${base}/`;
  return page <= 1 ? root : `${root}page/${page}/`;
}

function parseCatalogue(html) {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const meta = doc.querySelector('[data-veille-index-meta], [data-veille-archive]');
  return {
    entries: collectArchive(doc),
    pages: Math.max(1, Number(meta?.getAttribute('data-pages')) || 1),
  };
}

async function fetchCataloguePage(base, page) {
  const response = await fetch(catalogUrl(base, page), { credentials: 'same-origin' });
  if (!response.ok) {
    throw new Error(`catalogue ${response.status}`);
  }
  return parseCatalogue(await response.text());
}

async function fetchCatalogueRange(base, fromPage, toPage) {
  const pages = [];
  for (let page = fromPage; page <= toPage; page += 1) {
    pages.push(page);
  }
  const chunks = await Promise.all(pages.map(async (page) => {
    try {
      const result = await fetchCataloguePage(base, page);
      return result.entries;
    } catch (error) {
      return [];
    }
  }));
  return chunks.flat();
}

async function loadCatalogueFrom(base) {
  const first = await fetchCataloguePage(base, 1);
  const rest = first.pages > 1
    ? await fetchCatalogueRange(base, 2, first.pages)
    : [];
  return dedupeByUrl([...first.entries, ...rest]);
}

async function loadCatalogue(seedEntries, archivePages) {
  try {
    return await loadCatalogueFrom(BODY_INDEX_PATH);
  } catch (error) {
    try {
      return await loadCatalogueFrom(INDEX_PATH);
    } catch (fallbackError) {
      if (archivePages > 1) {
        const rest = await fetchCatalogueRange(archivePath(), 2, archivePages);
        return dedupeByUrl([...seedEntries, ...rest]);
      }
      return seedEntries;
    }
  }
}

function uniqueMonths(entries) {
  const seen = new Map();
  entries.forEach((entry) => {
    if (entry.month && !seen.has(entry.month)) {
      seen.set(entry.month, entry.monthLabel);
    }
  });
  return [...seen.entries()].sort((a, b) => b[0].localeCompare(a[0]));
}

function entryHaystack(entry) {
  return [entry.title, entry.excerpt, entry.body].filter(Boolean).join(' ').toLowerCase();
}

function applyFilters(entries, { q, month, sort }) {
  const needle = q.toLowerCase();
  const filtered = entries.filter((entry) => {
    const matchesQuery = !needle || entryHaystack(entry).includes(needle);
    const matchesMonth = !month || entry.month === month;
    return matchesQuery && matchesMonth;
  });
  filtered.sort((a, b) => {
    if (sort === 'ancien') {
      return a.date.localeCompare(b.date);
    }
    return b.date.localeCompare(a.date);
  });
  return filtered;
}

function groupByMonth(entries, sort) {
  const groups = new Map();
  entries.forEach((entry) => {
    if (!groups.has(entry.month)) {
      groups.set(entry.month, { month: entry.month, label: entry.monthLabel, items: [] });
    }
    groups.get(entry.month).items.push(entry);
  });
  const keys = [...groups.keys()].sort((a, b) => (
    sort === 'ancien' ? a.localeCompare(b) : b.localeCompare(a)
  ));
  return keys.map((key) => groups.get(key));
}

function packCompleteMonths(monthGroups, budget = PAGE_BUDGET) {
  const pages = [];
  let current = [];
  let count = 0;

  monthGroups.forEach((group) => {
    const size = group.items.length;
    if (current.length > 0 && count + size > budget) {
      pages.push(current);
      current = [];
      count = 0;
    }
    current.push(group);
    count += size;
  });

  if (current.length > 0) {
    pages.push(current);
  }
  return pages;
}

function renderEntry(entry) {
  const article = document.createElement('article');
  article.className = 'gh-veille-item';
  article.dataset.month = entry.month;
  article.dataset.monthLabel = entry.monthLabel;

  const link = document.createElement('a');
  link.href = entry.url;
  link.className = 'block mb-2 article-link';

  const title = document.createElement('h3');
  title.className = 'font-semibold text-gray-700 transition-colors duration-200 title-animation-underline';
  title.textContent = entry.title;

  const meta = document.createElement('div');
  meta.className = 'text-gray-600 mt-2';

  const date = document.createElement('span');
  date.className = 'text-sm';
  date.textContent = entry.displayDate;

  const bull = document.createElement('span');
  bull.className = 'bull';
  bull.textContent = '•';

  const reading = document.createElement('span');
  reading.className = 'text-sm';
  reading.textContent = entry.reading;

  meta.append(date, bull, reading);
  link.append(title, meta);
  article.append(link);
  return article;
}

function renderPagination(nav, state, pageCount, onPage) {
  nav.replaceChildren();
  if (pageCount <= 1) {
    nav.hidden = true;
    return;
  }

  nav.hidden = false;

  const prevSlot = document.createElement('div');
  prevSlot.className = 'pagination-slot pagination-slot--prev';
  if (state.page > 1) {
    const prev = document.createElement('a');
    prev.className = 'newer-posts';
    prev.href = archiveUrl({ ...state, page: state.page - 1 });
    prev.textContent = '← Plus récentes';
    prev.addEventListener('click', (event) => {
      event.preventDefault();
      onPage(state.page - 1);
    });
    prevSlot.append(prev);
  }

  const indicator = document.createElement('span');
  indicator.className = 'page-number';
  indicator.textContent = `Page ${state.page} sur ${pageCount}`;

  const nextSlot = document.createElement('div');
  nextSlot.className = 'pagination-slot pagination-slot--next';
  if (state.page < pageCount) {
    const next = document.createElement('a');
    next.className = 'older-posts';
    next.href = archiveUrl({ ...state, page: state.page + 1 });
    next.textContent = 'Plus anciennes →';
    next.addEventListener('click', (event) => {
      event.preventDefault();
      onPage(state.page + 1);
    });
    nextSlot.append(next);
  }

  nav.append(prevSlot, indicator, nextSlot);
}

export default function initVeilleArchive() {
  const toolbar = document.querySelector('[data-veille-toolbar]');
  const serverFeed = document.querySelector('[data-veille-feed]');
  const results = document.querySelector('[data-veille-results]');
  const serverPagination = document.querySelector('[data-veille-server-pages]');
  const pagesNav = document.querySelector('[data-veille-pages]');
  const status = document.querySelector('[data-veille-status]');
  const searchInput = toolbar?.querySelector('[name="q"]');
  const monthSelect = toolbar?.querySelector('[name="mois"]');
  const sortSelect = toolbar?.querySelector('[name="tri"]');
  const resetButton = toolbar?.querySelector('[data-veille-reset]');

  if (!toolbar || !results || !searchInput || !monthSelect || !sortSelect || !pagesNav) {
    return;
  }

  toolbar.hidden = false;
  let entries = collectArchive(serverFeed);

  function readFormState() {
    return {
      q: searchInput.value.trim(),
      month: monthSelect.value,
      sort: sortSelect.value,
      page: currentPage,
    };
  }

  function fillMonths(list) {
    const selected = monthSelect.value;
    [...monthSelect.querySelectorAll('option')].forEach((option) => {
      if (option.value) {
        option.remove();
      }
    });
    uniqueMonths(list).forEach(([value, label]) => {
      const option = document.createElement('option');
      option.value = value;
      option.textContent = label;
      monthSelect.append(option);
    });
    monthSelect.value = selected;
    if (monthSelect.value !== selected) {
      monthSelect.value = '';
    }
  }

  fillMonths(entries);

  let debounceId;
  let currentPage = readParams().page;

  function goToPage(page) {
    currentPage = page;
    sync({ history: 'push' });
    toolbar.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }

  function applyFromUrl() {
    const next = readParams();
    searchInput.value = next.q;
    monthSelect.value = next.month;
    sortSelect.value = next.sort;
    currentPage = next.page;
    sync({ history: 'none' });
  }

  function sync({ history = 'replace' } = {}) {
    const state = readFormState();
    const filtered = isFiltered(state);
    toolbar.classList.toggle('is-active', filtered);
    resetButton.hidden = !filtered;

    const matches = applyFilters(entries, state);
    const pages = packCompleteMonths(groupByMonth(matches, state.sort));
    const pageCount = Math.max(pages.length, 1);
    if (currentPage > pageCount) {
      currentPage = pageCount;
      state.page = currentPage;
    }
    if (history !== 'none') {
      writeParams(state, { push: history === 'push' });
    }

    const pageGroups = pages[currentPage - 1] || [];
    const pageEntries = pageGroups.flatMap((group) => group.items);

    results.replaceChildren();
    if (matches.length === 0) {
      const empty = document.createElement('p');
      empty.className = 'gh-veille-empty';
      empty.textContent = 'Aucune veille ne correspond à cette recherche.';
      results.append(empty);
    } else {
      pageEntries.forEach((entry) => results.append(renderEntry(entry)));
      groupVeilleByMonth(results);
    }

    results.hidden = false;
    if (serverFeed) {
      serverFeed.hidden = true;
    }
    if (serverPagination) {
      serverPagination.hidden = true;
    }

    renderPagination(pagesNav, state, pageCount, goToPage);

    if (status) {
      status.hidden = false;
      status.textContent = filtered
        ? `${veilleLabel(matches.length)} sur ${entries.length}`
        : veilleLabel(entries.length);
    }
  }

  searchInput.addEventListener('input', () => {
    window.clearTimeout(debounceId);
    debounceId = window.setTimeout(() => {
      currentPage = 1;
      sync({ history: 'replace' });
    }, 150);
  });
  monthSelect.addEventListener('change', () => {
    currentPage = 1;
    sync({ history: 'push' });
  });
  sortSelect.addEventListener('change', () => {
    currentPage = 1;
    sync({ history: 'push' });
  });
  toolbar.addEventListener('submit', (event) => {
    event.preventDefault();
    window.clearTimeout(debounceId);
    currentPage = 1;
    sync({ history: 'push' });
  });
  resetButton?.addEventListener('click', () => {
    searchInput.value = '';
    monthSelect.value = '';
    sortSelect.value = 'recent';
    currentPage = 1;
    sync({ history: 'push' });
  });
  window.addEventListener('popstate', applyFromUrl);

  const initial = readParams();
  searchInput.value = initial.q;
  monthSelect.value = initial.month;
  sortSelect.value = initial.sort;
  currentPage = initial.page;
  sync({ history: 'replace' });

  const archivePages = Math.max(
    1,
    Number(document.querySelector('[data-veille-archive]')?.dataset.pages) || 1,
  );
  loadCatalogue(entries, archivePages).then((full) => {
    if (full.length === entries.length) {
      const same = full.every((entry, index) => entry.url === entries[index]?.url && entry.body === entries[index]?.body);
      if (same) {
        return;
      }
    }
    entries = full;
    fillMonths(entries);
    sync({ history: 'none' });
  });
}
